import { useMemo } from 'react';
import dayjs from 'dayjs';
import { useTheme } from '@emotion/react';
import { Trans } from '@lingui/react/macro';
import { Alert } from 'src/components/ui';
import { useDarkMode } from '../../../context/DarkModeContext';

// dayjs, not new Date(): a date-only "YYYY-MM-DD" due date must be read as a local calendar day.
// new Date() reads it as UTC midnight, which is the previous day west of UTC (off by one in PR).
export function daysUntil(dueDate: string | undefined): number | null {
  if (!dueDate) return null;
  return dayjs(dueDate)
    .startOf('day')
    .diff(dayjs().startOf('day'), 'day');
}

export function FilingStatusCard({ completedCount, totalCount, dueDate }: { completedCount: number; totalCount: number; dueDate?: string }) {
  const { isDarkMode } = useDarkMode();
  const theme = useTheme() as { successMain: string };
  const daysRemaining = useMemo(() => daysUntil(dueDate), [dueDate]);
  const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  return (
    <Alert color="info">
      <div className="flex justify-between items-baseline mb-2 gap-2 flex-wrap">
        <span className="font-bold">
          <Trans>Filing status</Trans>
        </span>
        <span className="small">
          <Trans>
            {completedCount} of {totalCount} schedules completed
          </Trans>
          {daysRemaining !== null && (
            <>
              {' ● '}
              <strong style={{ color: isDarkMode ? '#f08080' : '#a32d2d' }}>{daysRemaining}</strong> <Trans>days until due date</Trans>
            </>
          )}
        </span>
      </div>
      <div
        style={{
          background: isDarkMode ? '#1c1e23' : 'white',
          height: '8px',
          borderRadius: '4px',
          overflow: 'hidden',
          border: `1px solid ${isDarkMode ? '#40444f' : '#d1d5db'}`,
        }}
      >
        <div style={{ background: theme.successMain, height: '100%', width: `${progressPercent}%`, transition: 'width 300ms ease' }} />
      </div>
    </Alert>
  );
}
